import { useState, useMemo } from 'react';
import moment from 'moment';
import {
  fetchTopChartsRetentionRankList,
  fetchTopChartsRetentionTrend,
} from '@/pages/intelligence/services/topcharts';
import { TopRPDRank } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { TableSelectionType } from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import { getCountryFlag } from '@/pages/home/_config/countries';
import {
  handleTableDefaultSelectGames,
  filterGamesWithRank,
} from '@/pages/intelligence/utils/tableUtils';
import {
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_FILTER_KEY,
} from '@intelligence/utils/urlPathCfg';

export interface FetchParamsType {
  /** 固定值 */
  entity_type: 'mobile';
  data_source: 'sensortower';
  order_source: 'sensortower';
  metrics: string[];
  genre_source: string;
  genre?: any[];
  sub_genre?: any[];
  iegg_genre?: any[];
  appannie_genre?: any[];
  sensortower_genre?: any[];
  company: string[];
  /** 国家筛选器参数 */
  region_type: string;
  regions: string[];
  /** 排序 */
  order_metric: string;
  order: 'desc' | 'asc';
  ratio_type: 0 | 1;
  /** 表格分页器参数 */
  page: number;
  page_size: number;
  /** 时间筛选器参数 */
  date_type: 'daily' | 'weekly' | 'monthly';
  date: string;
  /** 搜索名称 */
  search_by_name: string;
  /** 获取指定游戏的id(比如搜索游戏，折线图请求指定游戏) */
  unified_ids: string[];
  sensortower_tag_filter: any[];
  filter_ids: string[];
  platforms: string[];
}

/** 这里处理页面交互逻辑
 * 1 Confirm接口请求
 * 2 lineType请求折线图
 * 3 checkbox可视化柱状图交互|或重新请求折线图
 * 4
 */

const useRpdModel = (props: any) => {
  const { filterValue, setFilterVale, hasUrlShortLink } = props;
  const [tableData, setTableData] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);

  // 柱状图数据
  const [barOrPieData, setBarOrPieData] = useState<TopRPDRank[]>([]);
  const [lineData, setLineData] = useState<any>({});
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);
  const [tableListForClearSearch, setTableListForClearSearch] = useState([]);
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);
  const [filterIdsForClearSearch, setFilterIdsForClearSearch] = useState<string[]>([]);

  // 默认有数据的时间
  const [hasDataDate, setHasDataDate] = useState('');

  // 默认有数据的时间（提供给日期筛选器使用，重置筛选器时，日期筛选器会读取这个配置进行重置）
  const rangeTimeComponentDefault = useMemo(() => {
    if (!hasDataDate) return {};
    const hasDataDateM = moment(hasDataDate);
    if (!hasDataDateM.isValid()) return {};
    return {
      date: [hasDataDateM.clone(), hasDataDateM.clone()],
      week: [hasDataDateM.clone().startOf('week'), hasDataDateM.clone().endOf('week')],
      month: [hasDataDateM.clone().startOf('month'), hasDataDateM.clone().endOf('month')],
      year: [hasDataDateM.clone().startOf('year'), hasDataDateM.clone().endOf('year')],
    };
  }, [hasDataDate]);

  const defaultFetchParams: FetchParamsType = {
    entity_type: 'mobile',
    data_source: 'sensortower',
    order_source: 'sensortower',
    metrics: ['mau', 'D2', 'D3', 'D7', 'D15', 'D31'],
    region_type: 'region',
    regions: ['global'],
    date: '',
    ratio_type: 0,
    order: 'desc',
    order_metric: 'mau',
    // genre: [],
    // sub_genre: [],
    genre_source: 'sensortower',
    genre: [],
    sub_genre: [],
    iegg_genre: [],
    appannie_genre: [],
    sensortower_genre: [],
    page: 1,
    // page_size: 100,
    page_size: 50,
    date_type: 'monthly',
    company: [],
    search_by_name: '',
    unified_ids: [],
    sensortower_tag_filter: [],
    filter_ids: [],
    platforms: ['appstore', 'googleplay'],
  };

  const [fetchParams, setFetchParams] = useState<FetchParamsType>(defaultFetchParams);
  // 是否重新勾选过游戏
  const [operatedGame, setOperatedGame] = useState(false);

  const getGameData = async (argums: {
    params: FetchParamsType;
    isCheckOrSearch: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues: any;
    filterSaveName?: string;
    // 是否为初始化调用
    isInitial?: boolean;
  }) => {
    const {
      params,
      isCheckOrSearch,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      filterSaveName,
      isInitial = false,
    } = argums;
    // 当时间为空时，等时间返回再更新，否则直接写token到接口
    const isUpdateShortLinkNow = !!params?.date;
    // 时间如果是空的，则需要更新时间筛选器
    const needUpdateTime = !params?.date;
    if (isUpdateShortLinkNow && hasUrlShortLink) {
      updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues });
    }
    try {
      setTableData([]);
      setFetchParams(params);
      const isSearch = params?.unified_ids?.length > 0;
      const res = await fetchTopChartsRetentionRankList(params);
      const list = res?.data?.rank_list || [];
      const total = res?.data?.total || list?.length; // 搜索不返回total用表格长度计算

      let checkGameList = tableSelection;
      if (params.page === 1 && !isCheckOrSearch) {
        // 过滤rank==0后的游戏可勾选展示可视化
        const filterNoRankGameList = filterGamesWithRank(list);
        /** 默认选择第一页的前6作为表格全选展示 */
        const defaultGames = handleTableDefaultSelectGames(
          filterNoRankGameList?.slice(0, TOPGAMENUM),
          'id',
        );
        setDefaultSelectionGames(defaultGames);
        /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
        if (!isCheckOrSearch) {
          setTableSelection(defaultGames);
          const barOrPieDataTmp = handleTableToBar(filterNoRankGameList);
          setBarOrPieData(barOrPieDataTmp?.slice(0, TOPGAMENUM));
          checkGameList = defaultGames;
        }
      }
      if (!isSearch) {
        // 有unified_ids表示是搜索，则不存页面数据
        setTableListForClearSearch(list);
        setTotalForClearSearch(total);
        setFilterIdsForClearSearch(res?.req?.filter_ids || params?.filter_ids || []);
      }

      setTableData(list);
      setTableTotal(total);

      /** 时间及新筛选器参数回填 */
      const date = res?.req?.date;
      const newFetchParams = {
        ...params,
        date: date || '',
        filter_ids: isSearch
          ? (params?.unified_ids?.length ? params.unified_ids : params?.filter_ids)
          : res?.req?.filter_ids,
      };
      if (isInitial) {
        setHasDataDate(date || '');
      }
      setFetchParams(newFetchParams);

      // 获取到时间后更新筛选器的值，并更新url短链接
      if (needUpdateTime && date) {
        const newFilterValue = updateTimeFilterValue({
          // hasUrlShortLinkVal,
          haveFilterSaveBtn: true,
          filterSaveName,
          filterValue: updateFilterValues,
          timeFilterValues: {
            type: newFetchParams.date_type,
            value: [newFetchParams.date, newFetchParams.date],
          },
        });
        setFilterVale(newFilterValue);
        // 如果是时间为空等时间返回更新 且有短链接功能，则更新短链接
        if (!isUpdateShortLinkNow && hasUrlShortLink) {
          updateShortLinkFilterId({ [URL_FILTER_KEY]: newFilterValue });
        }
      }

      if (isUpdateLineChart) {
        if (checkGameList.length > 0) {
          const idList = checkGameList.map((t) => t?.gameId);
          getLineChartData({ ...params, unified_ids: idList || [] });
        } else {
          setLineData([]);
        }
      }
      return res;
    } catch (err) {
      return null;
    }
  };

  const getLineChartData = async (params: FetchParamsType) => {
    try {
      if (!params?.unified_ids?.length) {
        throw new Error();
      }
      setLineData({});
      const res = await fetchTopChartsRetentionTrend(params);
      // console.log('lineDataRes', lineDataRes);
      const lineDataRes = res?.data || {};
      const temp = {
        mau: Array.isArray(lineDataRes.mau) ? lineDataRes.mau : [],
        D2: Array.isArray(lineDataRes.D2) ? lineDataRes.D2 : [],
        D3: Array.isArray(lineDataRes.D3) ? lineDataRes.D3 : [],
        D7: Array.isArray(lineDataRes.D7) ? lineDataRes.D7 : [],
        D15: Array.isArray(lineDataRes.D15) ? lineDataRes.D15 : [],
        D31: Array.isArray(lineDataRes.D31) ? lineDataRes.D31 : [],
      };
      // console.log('temp', temp);
      setLineData(temp);
    } catch (e) {
      console.error(e);
    } finally {
    }
  };

  const handleTableToBar = (list: any[], end = TOPGAMENUM) => {
    if (!Array.isArray(list)) {
      return [];
    }
    // TODO 将表格数据转化为柱状图数据
    return list.slice(0, end);
  };

  const isNodata = useMemo(() => {
    if (tableTotal < 1 || tableSelection.length < 1) {
      return true;
    }
    return false;
  }, [tableTotal, tableSelection]);

  const handleClearSearch = () => {
    setFetchParams({
      ...fetchParams,
      unified_ids: [],
      filter_ids: filterIdsForClearSearch,
    });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const transTableDataForSearch = (res: any) => {
    const resultList = res?.data?.rank_list;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    const tranksList: RecordItemType[] = resultList.map((item) => ({
      id: item?.id,
      imgSrc: item?.cover,
      lineOneStr: item?.name,
      lineTwoImg: getCountryFlag(item?.publisher_addr),
      lineTwoImgName: item?.publisher_addr,
      lineTwoStr: item?.publisher,
    }));
    return tranksList;
  };

  return {
    // tableLoading,
    // chartLoading,
    tableTotal,
    tableData,
    barOrPieData,
    setBarOrPieData,
    lineData,
    fetchParams,
    defaultFetchParams,
    getGameData,
    tableSelection,
    defaultSelectionGames,
    getLineChartData,
    operatedGame,
    setOperatedGame,
    isNodata,
    setTableSelection,
    handleClearSearch,
    transTableDataForSearch,
    rangeTimeComponentDefault,
  };
};

export default useRpdModel;
